import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { replayApi, type GameReplay } from '../../services/replayApi';
import './ReplayViewer.css';

const ReplayViewer: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  
  const [replay, setReplay] = useState<GameReplay | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (gameId) {
      loadReplay();
    }
  }, [gameId]);

  // Auto-play logic
  useEffect(() => {
    if (isPlaying && replay && currentIndex < replay.snapshots.length - 1) {
      const timer = setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
      }, 1000 / playbackSpeed);
      
      return () => clearTimeout(timer);
    } else if (isPlaying && currentIndex >= (replay?.snapshots.length || 0) - 1) {
      setIsPlaying(false);
    }
  }, [isPlaying, currentIndex, playbackSpeed, replay]);

  const loadReplay = async () => {
    if (!gameId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await replayApi.getReplay(gameId);
      setReplay(data);
    } catch (err) {
      console.error('Failed to load replay:', err);
      setError(err instanceof Error ? err.message : 'Failed to load replay');
    } finally {
      setLoading(false);
    }
  };

  const currentSnapshot = replay?.snapshots[currentIndex];

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handlePrevious = () => {
    setCurrentIndex(Math.max(0, currentIndex - 1));
    setIsPlaying(false);
  };

  const handleNext = () => {
    if (replay) {
      setCurrentIndex(Math.min(replay.snapshots.length - 1, currentIndex + 1));
      setIsPlaying(false);
    }
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsPlaying(false);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentIndex(parseInt(e.target.value));
    setIsPlaying(false);
  };

  if (loading) {
    return (
      <div className="replay-viewer">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading replay...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="replay-viewer">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={() => navigate('/replays')} className="back-btn">Back to Replays</button>
        </div>
      </div>
    );
  }

  if (!replay) {
    return (
      <div className="replay-viewer">
        <div className="empty-state">
          <div className="empty-icon">🎬</div>
          <h2>Replay Not Found</h2>
          <button onClick={() => navigate('/replays')} className="back-btn">Back to Replays</button>
        </div>
      </div>
    );
  }

  return (
    <div className="replay-viewer">
      {/* Header */}
      <div className="replay-header">
        <button onClick={() => navigate('/replays')} className="back-button">← Back</button>
        <h1>🎬 Game Replay</h1>
        <div className="replay-info">
          <span>{replay.gameType}</span>
          <span>•</span>
          <span>{replayApi.formatTimestamp(replay.completedAt)}</span>
          <span>•</span>
          <span>{replayApi.formatDuration(replay.duration)}</span>
        </div>
      </div>

      {/* Game State Display */}
      <div className="game-state-display">
        {currentSnapshot && (
          <>
            <div className="state-header">
              <div className="round-info">
                Round {currentSnapshot.roundNumber} • Phase: {currentSnapshot.currentPhase}
              </div>
              <div className="snapshot-progress">
                Snapshot {currentIndex + 1} of {replay.snapshots.length}
              </div>
            </div>

            {/* Players */}
            <div className="players-grid">
              {currentSnapshot.players.map((player, idx) => (
                <div 
                  key={idx} 
                  className={`player-card ${idx === currentSnapshot.currentPlayerIndex ? 'active' : ''}`}
                >
                  <div className="player-header">
                    <span className="player-name">{player.username}</span>
                    {idx === currentSnapshot.currentPlayerIndex && <span className="current-marker">→</span>}
                  </div>
                  <div className="player-stats">
                    <div className="stat">
                      <span className="stat-label">Cards:</span>
                      <span className="stat-value">{player.hand.length}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Tricks:</span>
                      <span className="stat-value">{player.tricksWon}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Score:</span>
                      <span className="stat-value">{player.score}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Current Trick */}
            {currentSnapshot.currentTrick.length > 0 && (
              <div className="current-trick-section">
                <h3>Current Trick</h3>
                <div className="trick-cards">
                  {currentSnapshot.currentTrick.map((play, idx) => {
                    const playerName = currentSnapshot.players.find(p => p.userId === play.playerId)?.username || 'Unknown';
                    return (
                      <div key={idx} className="trick-card">
                        <div className="card-display">{play.card}</div>
                        <div className="card-player">{playerName}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Trump Suit */}
            {currentSnapshot.trumpSuit && (
              <div className="trump-display">
                <span className="trump-label">Trump:</span>
                <span className="trump-suit">{currentSnapshot.trumpSuit}</span>
              </div>
            )}

            {/* Bidding Info */}
            {currentSnapshot.biddingInfo && currentSnapshot.biddingInfo.bids.length > 0 && (
              <div className="bidding-section">
                <h3>Bidding</h3>
                <div className="bids-list">
                  {currentSnapshot.biddingInfo.bids.map((bid, idx) => {
                    const playerName = currentSnapshot.players.find(p => p.userId === bid.playerId)?.username || 'Unknown';
                    return (
                      <div key={idx} className="bid-item">
                        <span className="bid-player">{playerName}:</span>
                        <span className="bid-value">{bid.bid}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Playback Controls */}
      <div className="playback-controls">
        <div className="timeline-container">
          <input
            type="range"
            min="0"
            max={replay.snapshots.length - 1}
            value={currentIndex}
            onChange={handleSliderChange}
            className="timeline-slider"
          />
          <div className="timeline-labels">
            <span>Start</span>
            <span>{currentIndex + 1} / {replay.snapshots.length}</span>
            <span>End</span>
          </div>
        </div>

        <div className="control-buttons">
          <button onClick={handleReset} className="control-btn" title="Reset to start">
            ⏮️
          </button>
          <button onClick={handlePrevious} className="control-btn" title="Previous">
            ⏪
          </button>
          <button onClick={handlePlayPause} className="control-btn play-pause" title={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? '⏸️' : '▶️'}
          </button>
          <button onClick={handleNext} className="control-btn" title="Next">
            ⏩
          </button>
        </div>

        <div className="speed-controls">
          <label>Speed:</label>
          {[0.5, 1, 1.5, 2].map(speed => (
            <button
              key={speed}
              onClick={() => setPlaybackSpeed(speed)}
              className={`speed-btn ${playbackSpeed === speed ? 'active' : ''}`}
            >
              {speed}x
            </button>
          ))}
        </div>
      </div>

      {/* Final Result */}
      <div className="final-result">
        <h3>🏆 Final Result</h3>
        <div className="winner-display">
          Winner: <strong>{replay.finalResult.winner}</strong>
        </div>
        <div className="scores-table">
          {replay.finalResult.scores.map((score, idx) => {
            const playerName = replay.playerNames[replay.playerIds.indexOf(score.userId)] || 'Unknown';
            return (
              <div key={idx} className="score-row">
                <span className="score-player">{playerName}</span>
                <span className="score-tricks">{score.tricksWon} tricks</span>
                <span className="score-points">{score.score} pts</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ReplayViewer;
